import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CASE_EVENT_KINDS,
  CASE_EVENT_KIND_MAP,
  CASE_STATUS_MAP,
  type CaseStatus,
} from '@goyone/shared';
import {
  Badge,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { date, dateTime, money, num } from '../../lib/format';
import { ActionDialog, type ActionRow, type CaseRow } from './Relations';

interface EventRow {
  id: string;
  kind: 'NOTE' | 'INTERVIEW' | 'EVIDENCE' | 'DECISION' | 'STATUS';
  kindLabel: string;
  kindKind: string;
  body: string;
  personId: string | null;
  at: string;
}

/*
 * عدّادا السجلّ والإجراءات في القائمة يصيران قائمتين في الملفّ.
 *
 * فيُستبدلان لا يُضافان: اسمٌ واحدٌ بمعنيين يجعل قارئَ الشيفرة يكتب
 * num() على مصفوفة.
 */
interface CaseView extends Omit<CaseRow, 'events' | 'actions'> {
  next: CaseStatus[];
  events: EventRow[];
  actions: ActionRow[];
}

interface EmployeeLite {
  id: string;
  name: string;
}

/**
 * ملفُّ حالة.
 *
 * ## سجلٌّ يُكتب ولا يُحذف منه
 *
 * من يقرؤه بعد سنتين — في تظلُّمٍ أو نزاعٍ قضائيّ — يحتاج أن يرى ما سُمع
 * ومتى وممّن. فلا زرَّ حذفٍ لواقعةٍ ولا تعديلَ لما كُتب: من أخطأ في كتابةِ
 * واقعةٍ يكتب بعدها واقعةً تصحّحها، وتبقى الاثنتان.
 *
 * ## والإغلاقُ في اتّجاهٍ واحد
 *
 * قرارٌ أُبلغ به أطرافُه لا يُعدَّل من ورائهم. ومن جدّ فيه جديدٌ تُفتح له
 * حالةٌ جديدةٌ تشير إلى الأولى.
 */
export function CaseDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [writing, setWriting] = useState(false);
  const [deciding, setDeciding] = useState<CaseStatus | null>(null);
  const [acting, setActing] = useState(false);

  const item = useQuery({
    queryKey: ['relations', 'case', id],
    queryFn: () => api<CaseView>(`/hr/relations/cases/${id}`),
  });

  const c = item.data;

  const acknowledge = useMutation({
    mutationFn: (actionId: string) =>
      api(`/hr/relations/actions/${actionId}/acknowledge`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
      toast('سُجّل إبلاغُه.');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <div className="page">
      <PageHead
        title={c ? `${c.number} — ${c.subject}` : 'حالة'}
        subtitle={
          c ? `${c.kindLabel} · فُتحت ${date(c.openedOn ?? '')}` : undefined
        }
        actions={
          c &&
          c.writable && (
            <div className="row" style={{ gap: 6 }}>
              <button className="btn" onClick={() => setWriting(true)}>
                <Icon name="note" size={14} />
                أضف واقعة
              </button>
              {c.disciplinary && (
                <button
                  className="btn btn-danger"
                  onClick={() => setActing(true)}
                >
                  أصدِر إجراءً
                </button>
              )}
              {c.next.map((s) => (
                <button
                  key={s}
                  className={s === 'DECIDED' ? 'btn btn-primary' : 'btn'}
                  onClick={() => setDeciding(s)}
                >
                  {CASE_STATUS_MAP[s].label}
                </button>
              ))}
            </div>
          )
        }
      />

      <Link className="link tiny" to="/hr/relations">
        <Icon name="arrowLeft" size={12} /> علاقات الموظفين
      </Link>

      {item.isError && (
        <ErrorState error={item.error} onRetry={() => void item.refetch()} />
      )}
      {item.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {c && (
        <>
          <div className="stats mt-3">
            <Stat
              label="الحالة"
              value={<Badge kind={c.statusKind}>{c.statusLabel}</Badge>}
              note={c.statusNote}
            />
            <Stat
              label="عمّن"
              value={c.about?.name ?? '—'}
              note={c.raisedBy ? `رفعها ${c.raisedBy.name}` : 'بلاغٌ بلا اسم'}
            />
            <Stat
              label="يتولّاها"
              value={c.assignedTo?.name ?? '—'}
              note={c.dueOn ? `يُبتُّ قبل ${date(c.dueOn)}` : 'بلا موعد'}
            />
            <Stat
              label="السجلّ"
              value={num(c.events.length)}
              note={`و${num(c.actions.length)} إجراءً`}
            />
          </div>

          {c.confidential && (
            <Notice title="سرّيّة" icon="eye" kind="warning">
              وسمٌ يُنبّه من يعرضها في اجتماع. وليس تعميةً في القاعدة: من يملك
              فتح هذه الشاشة يقرؤها كاملة.
            </Notice>
          )}

          {c.overdue && (
            <Notice title="مضى موعدُ البتِّ فيها" icon="alert" kind="warning">
              كان يُنتظر قرارٌ قبل {date(c.dueOn ?? '')}. ومن رفع شكوى ولم يُردّ
              عليه يتعلّم ألّا يرفع.
            </Notice>
          )}

          <div className="card mt-3">
            <h2 className="card-title">ما وقع</h2>
            <p className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
              {c.description}
            </p>
          </div>

          {c.decision && (
            <div className="card mt-3">
              <h2 className="card-title">القرار</h2>
              <p className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                {c.decision}
              </p>
            </div>
          )}

          {c.closeNote && (
            <div className="card mt-3">
              <h2 className="card-title">لماذا أُغلقت</h2>
              <p className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                {c.closeNote}
              </p>
            </div>
          )}

          {/* ---------- الإجراءات ---------- */}
          {c.actions.length > 0 && (
            <div className="card mt-3">
              <h2 className="card-title">الإجراءات الصادرة</h2>
              <div className="table-wrap mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الموظّف</th>
                      <th>الإجراء</th>
                      <th>صدر في</th>
                      <th>أُبلغ</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {c.actions.map((a) => (
                      <tr key={a.id}>
                        <td>{a.employee.name}</td>
                        <td>
                          <Badge kind={a.kindKind}>{a.kindLabel}</Badge>
                          {a.amount !== null && (
                            <div className="tiny">
                              {money(a.amount, a.currency)}
                              {a.posted ? ' · نُفِّذ' : ' · لم يُنفَّذ'}
                            </div>
                          )}
                        </td>
                        <td className="tiny">{date(a.issuedOn ?? '')}</td>
                        <td className="tiny">
                          {a.acknowledgedAt ? date(a.acknowledgedAt) : '—'}
                        </td>
                        <td>
                          {!a.acknowledgedAt && (
                            <button
                              className="btn btn-sm"
                              disabled={acknowledge.isPending}
                              onClick={() => acknowledge.mutate(a.id)}
                            >
                              سجّل إبلاغَه
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/*
                الإبلاغُ يُسجَّل بيدٍ لأنّ الموظّف لا يفتح هذه الشاشة: ما
                يُحفظ أنّ من يكتب سجّل أنّه أُبلغ ووقّع — لا توقيعًا
                إلكترونيًّا لا يصمد أمام أوّل نزاع.
              */}
              <p className="tiny mute mt-2">
                الإبلاغُ واقعةٌ تُسجَّل بيد: لا توقيعَ إلكترونيًّا في هذا
                النظام، وما يُحفظ أنّ من يكتب سجّل أنّ صاحبَه أُبلغ ووقّع.
              </p>
            </div>
          )}

          {/* ---------- السجلّ ---------- */}
          <div className="card mt-3">
            <h2 className="card-title">السجلّ</h2>
            <div className="mt-2">
              {c.events.map((e) => (
                <div key={e.id} className="mb-3">
                  <div className="row" style={{ gap: 6 }}>
                    <Badge kind={e.kindKind}>{e.kindLabel}</Badge>
                    <span className="tiny mute">{dateTime(e.at)}</span>
                  </div>
                  <p className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                    {e.body}
                  </p>
                </div>
              ))}
            </div>
            <p className="tiny mute mt-2">
              يُكتب ولا يُحذف منه شيء: من أخطأ في واقعةٍ يكتب بعدها واقعةً
              تصحّحها، وتبقى الاثنتان. وهذا ما يجعله سندًا.
            </p>
          </div>
        </>
      )}

      {writing && <EventDialog caseId={id} onClose={() => setWriting(false)} />}
      {acting && c && (
        <ActionDialog
          caseId={id}
          employeeId={c.about?.id}
          onClose={() => setActing(false)}
        />
      )}
      {deciding && (
        <StatusDialog
          caseId={id}
          status={deciding}
          onClose={() => setDeciding(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   إضافة واقعة
   ========================================================================== */

function EventDialog({
  caseId,
  onClose,
}: {
  caseId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [kind, setKind] = useState<'NOTE' | 'INTERVIEW' | 'EVIDENCE'>('NOTE');
  const [body, setBody] = useState('');
  const [personId, setPersonId] = useState('');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
    enabled: kind === 'INTERVIEW',
  });

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/relations/cases/${caseId}/events`, {
        method: 'POST',
        body: { kind, body, personId: personId || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
      toast('أُضيفت الواقعة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  /* «قرار» و«تغيّرت الحالة» يكتبهما النظامُ عن واقعةٍ وقعت — لا تُكتبان بيد. */
  const manual = CASE_EVENT_KINDS.filter((k) => CASE_EVENT_KIND_MAP[k].manual);

  return (
    <Modal
      title="واقعة في السجلّ"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || body.trim().length < 5}
            onClick={() => save.mutate()}
          >
            أضِفها
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label" htmlFor="ce-kind">
            النوع
          </label>
          <select
            id="ce-kind"
            className="select"
            value={kind}
            onChange={(e) =>
              setKind(e.target.value as 'NOTE' | 'INTERVIEW' | 'EVIDENCE')
            }
          >
            {manual.map((k) => (
              <option key={k} value={k}>
                {CASE_EVENT_KIND_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {CASE_EVENT_KIND_MAP[kind].description}
          </span>
        </div>

        {kind === 'INTERVIEW' && (
          <div className="field span-2">
            <label className="label" htmlFor="ce-person">
              من سُمع
            </label>
            <select
              id="ce-person"
              className="select"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
            >
              <option value="">— لم يُذكر —</option>
              {(staff.data?.items ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field span-2">
          <label className="label req" htmlFor="ce-body">
            ما جرى
          </label>
          <textarea
            id="ce-body"
            className="input"
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              kind === 'INTERVIEW'
                ? 'ما قاله كما قاله — لا خلاصةً لما فهمتَه'
                : 'ما وقع، ومتى'
            }
          />
          <span className="tiny mute">
            ما يُكتب هنا لا يُعدَّل ولا يُحذف — وهذا ما يجعله سندًا.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   نقل الحالة
   ========================================================================== */

function StatusDialog({
  caseId,
  status,
  onClose,
}: {
  caseId: string;
  status: CaseStatus;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/relations/cases/${caseId}/status`, {
        method: 'POST',
        body: { status, note: note || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
      toast('حُفظ.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const needsNote = status === 'DECIDED';
  const needsReason = status === 'CLOSED' || status === 'WITHDRAWN';
  const min = needsNote ? 20 : needsReason ? 10 : 0;

  return (
    <Modal
      title={CASE_STATUS_MAP[status].label}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || note.trim().length < min}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      {(status === 'CLOSED' || status === 'WITHDRAWN') && (
        <Notice title="ولا رجعةَ بعده" icon="alert" kind="warning">
          ما أُغلق لا يُعاد فتحُه: قرارٌ أُبلغ به أطرافُه لا يُعدَّل من ورائهم.
          ومن جدّ فيه جديدٌ تُفتح له حالةٌ جديدةٌ تشير إلى هذه.
        </Notice>
      )}

      <div className="field mt-3">
        <label className={min > 0 ? 'label req' : 'label'} htmlFor="cs-note">
          {needsNote ? 'القرار وسببُه' : needsReason ? 'لماذا' : 'ملاحظة'}
        </label>
        <textarea
          id="cs-note"
          className="input"
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            needsNote
              ? 'ما قُرِّر، وعلى أيّ شيءٍ بُني، ومن قرّره'
              : 'ما انتهت إليه'
          }
        />
        {min > 0 && (
          <span className="tiny mute">
            يُكتب في السجلّ ويُقرأ بعد سنتين — لا كلمةً واحدة.
          </span>
        )}
      </div>
    </Modal>
  );
}
